import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Package, ShoppingBag, ArrowRight, Home, FileText, Sparkles } from "lucide-react";
import { ordersApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";

interface OrderDetails {
  id: number;
  order_number: string;
  razorpay_order_id: string | null;
  total_amount: number;
  status: string;
  payment_status: string;
  created_at: string;
  items: Array<{
    id: number;
    product_id: number;
    product_name: string;
    quantity: number;
    price: number;
    subtotal: number;
  }>;
  address: {
    name: string;
    phone: string;
    address_line1: string;
    address_line2: string | null;
    city: string;
    state: string;
    pincode: string;
    country: string;
  } | null;
}

export default function PaymentSuccessPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuthStore();
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);

  const orderId = searchParams.get("order_id");
  const orderNumber = searchParams.get("order_number");

  useEffect(() => {
    // If no order ID in URL, redirect to home
    if (!orderId && !orderNumber) {
      toast.error("Invalid order reference");
      navigate("/");
      return;
    }

    // Fetch order details if order ID is provided
    const fetchOrderDetails = async () => {
      if (orderId && token) {
        try {
          const response = await ordersApi.getById(parseInt(orderId), token);
          // Handle wrapped response format
          const order = response.data || response;
          setOrderDetails(order);
        } catch (error: any) {
          console.error("Failed to fetch order details:", error);
          toast.error("Failed to load order details");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, orderNumber, navigate]);

  if (loading) {
    return (
      <main className="flex-1 flex items-center justify-center py-20 px-4 pt-24">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </main>
    );
  }

  const displayOrderNumber = orderDetails?.order_number || orderNumber || "N/A";
  const displayOrderId = orderDetails?.id || orderId || "N/A";

  return (
    <main className="flex-1 py-12 px-4 pt-24 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6"
            >
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </motion.div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Payment Successful!
            </h1>
            <p className="text-lg text-gray-600 mb-2">
              Thank you for your purchase. Your order has been confirmed.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg">
              <FileText className="w-4 h-4" />
              <span className="text-sm font-medium">Order Reference: {displayOrderNumber}</span>
            </div>
          </motion.div>

          {/* Order Summary Card */}
          {orderDetails && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
            >
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold text-gray-900">Order Summary</h2>
              </div>

              {/* Order Items */}
              <div className="space-y-3 mb-6">
                {orderDetails.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                      <p className="text-xs text-gray-500">Quantity: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">
                      ₹{item.subtotal.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              {/* Order Total */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Subtotal</span>
                  <span className="text-sm font-medium text-gray-900">
                    ₹{orderDetails.items.reduce((sum, item) => sum + item.subtotal, 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Total Amount</span>
                  <span className="text-lg font-bold text-gray-900">
                    ₹{orderDetails.total_amount.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Delivery Address */}
              {orderDetails.address && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Delivery Address</h3>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p className="font-medium text-gray-900">{orderDetails.address.name}</p>
                    <p>{orderDetails.address.address_line1}</p>
                    {orderDetails.address.address_line2 && <p>{orderDetails.address.address_line2}</p>}
                    <p>
                      {orderDetails.address.city}, {orderDetails.address.state} - {orderDetails.address.pincode}
                    </p>
                    <p>{orderDetails.address.country}</p>
                    <p className="mt-2">Phone: {orderDetails.address.phone}</p>
                  </div>
                </div>
              )}

              {/* Order Status */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Order Status</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium uppercase">
                    {orderDetails.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-gray-600">Payment Status</span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium uppercase">
                    {orderDetails.payment_status}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {/* What's Next Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100 p-6 mb-6"
          >
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">What's Next?</h3>
                <ul className="text-sm text-gray-700 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-0.5">•</span>
                    <span>You will receive an order confirmation email shortly.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-0.5">•</span>
                    <span>We'll notify you once your order is dispatched.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 mt-0.5">•</span>
                    <span>Track your order status in your account dashboard.</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-3"
          >
            {orderDetails && (
              <Link
                to={`/account?tab=orders`}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium"
              >
                <FileText className="w-4 h-4" />
                View Order Details
              </Link>
            )}
            <Link
              to="/products"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
            >
              <ShoppingBag className="w-4 h-4" />
              Continue Shopping
            </Link>
            <Link
              to="/"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
          </motion.div>

          {/* Help Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="mt-8 text-center"
          >
            <p className="text-sm text-gray-500 mb-2">
              Need help? <Link to="/contact" className="text-gray-900 font-medium hover:underline">Contact us</Link>
            </p>
            <p className="text-xs text-gray-400">
              Order Reference: {displayOrderNumber} {orderId && `(ID: ${displayOrderId})`}
            </p>
          </motion.div>
        </div>
      </main>
  );
}

